<template>
  <div :class="_class">
    <transition name="show-bg-overlay">
      <div
        v-if="helpDrawer"
        class="cat-page-help-bg-overlay"
        @click="bgClick"
      ></div>
    </transition>
    <div v-if="helpExists" class="page-help-button-wrapper" :class="className">
      <a
        v-if="!sectionHelp && helpExists"
        ref="button"
        role="button"
        class="page-help-button"
        tabindex="0"
        @click="show"
        @keydown.enter="show"
        @keydown.space="show"
        ><cat-icon icon="help-circled" decorative />{{ buttonCaption }}</a
      >
    </div>
    <cat-help-panel :id="id" :open="helpDrawer" :url="url" @ui-close="close">
      <slot></slot>
    </cat-help-panel>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject, computed } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatHelpPanel from "./catHelpPanel.vue";
import ModalMixin from "../utilities/modalMixin.js";
import { checkHelpSource } from "@/components/catPageTemplate/pageHelpMixin";
import CatIcon from "@/components/utilities/catIcon.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const suppressExternalHelp = inject("suppressExternalHelp", false);
    const buttonCaption = computed(() => {
      return resolveTxt("skin.page.help.caption", "Help");
    });

    return {
      resolveTxt,
      suppressExternalHelp,
      buttonCaption
    };
  },
  name: "CatPageHelp",
  mixins: [ModalMixin],
  components: {
    CatHelpPanel,
    CatIcon
  },
  props: {
    id: {
      type: [String, Number],
      description: "Page help ID."
    },
    className: {
      type: String,
      description:
        "Class name to be added to the page help button wrapper markup."
    },
    sectionHelp: {
      type: Boolean,
      default: false,
      description:
        "Indicates if the content is for a section and not for the whole page/service."
    },
    title: {
      type: String,
      description:
        "Accessible name to be used to identify page help component. The title will be appended with the text key skin.page.help.caption or defaults to 'Page Help'."
    },
    sourceId: {
      type: String,
      description: "Trigger source ID."
    },
    url: {
      type: String,
      description: "External help content URL."
    },
    useContentSlot: {
      type: Boolean,
      default: false,
      description:
        "Indicates if a content for the help content slot is provided. If enabled, the external help content URL is not used."
    },
    open: {
      type: Boolean,
      default: false,
      description: "Indicates if the help panel is displayed."
    }
  },
  data: function () {
    return {
      helpDrawer: this.open,
      helpExists: false,
      dataUrl: this.url
    };
  },
  mounted: function () {
    this.checkHelp();
  },
  watch: {
    open: function (newval, oldval) {
      this.helpDrawer = newval;
      if (this.helpDrawer) {
        if (typeof this.addBodyClass === "function") {
          this.addBodyClass();
        }
      }
    },
    url: function (newval, oldval) {
      this.dataUrl = newval;
    }
  },
  computed: {
    _class: function () {
      return {
        "cat-page-help": true,
        "section-help": this.sectionHelp
      };
    }
  },
  methods: {
    checkHelp: function () {
      if (this.suppressExternalHelp) {
        this.helpExists = false;
        return;
      }

      if (!this.dataUrl) {
        // Assume no url they have content in the slot
        this.helpExists = this.useContentSlot;
        return;
      }
      var _t = this;
      var cb = function (exists) {
        _t.helpExists = exists;
      };
      checkHelpSource(this.dataUrl, cb);
    },
    toggleHelp: function (evt) {
      if (!this.helpDrawer) {
        this.show(evt);
      } else {
        this.close(evt);
      }
    },
    show: function (e) {
      if (typeof this.addBodyClass === "function") {
        this.addBodyClass();
      }
      this.helpDrawer = true;
      var detail = {
        url: this.dataUrl,
        sourceTitle: this.buttonCaption
      };

      this.$emit("ui-show", detail);
      var evt = new CustomEvent("ui-show-page-help", {
        detail: detail
      });
      document.dispatchEvent(evt);
    },
    close: function (e) {
      if (typeof this.removeBodyClass === "function") {
        this.removeBodyClass();
      }
      this.helpDrawer = false;
      this.$emit("ui-close", e);
      this.focusTrigger();
    },
    bgClick: function (e) {
      this.$emit("ui-bg-click", e);
      e.preventDefault();

      this.close(e);
    },
    focusTrigger: function () {
      let helpButton = document.getElementById(this.sourceId);

      // Focus user to the trigger button
      if (this.sourceId && helpButton) {
        helpButton.focus();
      } else {
        let pageHelpButton =
          document.getElementsByClassName("page-help-button");
        if (pageHelpButton) {
          pageHelpButton[0].focus();
        }
      }
    }
  }
});
</script>
